import { FC, useEffect, useRef } from 'react';

import { IconLanguage, IconPrinter, IconShare2 } from '@tabler/icons-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Tag, Typography } from '@cfa/react-components';
import { IconButton } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import { useTapSentinel } from '@/hooks/useTapSentinel';

import { getCountryLabel } from '@/utils/document-utils/countries';
import { printTridionDocument } from '@/utils/print';
import { cn } from '@/utils/tailwind';

import BreadcrumbLinks from '@/components/BreadcrumbLinks/BreadcrumbLinks';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { Breadcrumb } from '@/types/types';

import { colorTag } from 'utils/formatDocumentTag';

import { documentPersistedAtom } from '@/atoms/document-persisted';
import {
  documentTransientAtom,
  toggleTranslationAtom,
} from '@/atoms/document-transient';
import { isTridionPreviewAtom } from '@/atoms/is-tridion-preview';
import { userPrefsAtom } from '@/atoms/userPrefs';

interface TridionTitleProps {
  breadcrumbs?: Breadcrumb[];
  tags?: string[];
  title?: string;
  /** Custom icon content to render */
  iconContent?: React.ReactNode;
}

const TridionTitle: FC<TridionTitleProps> = ({
  breadcrumbs = [],
  tags = [],
  title = '',
  iconContent,
}) => {
  const { chooseDocumentVersion } = useFlags();
  const { t } = useTranslation();
  const toggleTranslation = useSetAtom(toggleTranslationAtom);
  const { hasTranslatedValue: showTranslateIcon } = useAtomValue(
    documentPersistedAtom,
  );
  const { chosenDocumentCountry } = useAtomValue(documentTransientAtom);
  const { country: userCountry } = useAtomValue(userPrefsAtom);
  const { data: auth } = useAuth();
  const isAdminOperatorOrStakeholder = auth?.isAdminOperatorOrStakeholder;

  const enableChooseDocumentVersion =
    chooseDocumentVersion && isAdminOperatorOrStakeholder;
  const documentCountry = chosenDocumentCountry ?? userCountry.id;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { markFired } = useTapSentinel(wrapperRef, {
    debug: 'Clicking on the print button in DocumentTitle for mobile',
  });

  const onCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {t('Generic.linkCopied')}
      </ToastMessageBlock>
    ));
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === 'p' ||
          event.charCode === 16 ||
          event.charCode === 112 ||
          event.keyCode === 80)
      ) {
        event.stopPropagation();
        event.preventDefault(); // Prevent default print dialog
        printTridionDocument();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const renderDocTags = () => {
    if (!tags.length) {
      return null;
    }
    return tags.map((tag, index) => (
      <Tag
        className="md:mr-2.5 md:font-normal"
        color={colorTag(tag)}
        key={index}
        label={tag}
        variant="filled"
      />
    ));
  };

  const renderAllTags = () => (
    <>
      {renderDocTags()}
      {!!documentCountry && enableChooseDocumentVersion && (
        <Tag
          className="md:mr-2.5 md:font-normal"
          color="secondaryBlue"
          key={documentCountry}
          label={getCountryLabel(documentCountry)}
          variant="outlined"
        />
      )}
    </>
  );

  const handlePrintMobile = () => {
    markFired();
    printTridionDocument();
  };

  const isTridionPreview = useAtomValue(isTridionPreviewAtom);

  return (
    <div className="shadow-elevation-16 relative w-full bg-white pb-12">
      {breadcrumbs.length > 0 && (
        <div className="px-10 pt-10 pb-0">
          <BreadcrumbLinks breadcrumbs={breadcrumbs} />
        </div>
      )}

      {/** Tags are on top of title on mobile */}
      <div className="flex justify-center gap-4 md:hidden">
        {renderAllTags()}
      </div>
      <div className="flex justify-center px-5 py-0 md:justify-start md:px-10">
        <Typography variant="h2" color="secondary" className="mt-3! mb-2!">
          <div style={{ display: 'inline', marginRight: '10px' }}>{title}</div>

          {/** Tags are to the right of title on desktop */}
          <div className="hidden md:inline">{renderAllTags()}</div>
        </Typography>
      </div>
      <div
        className={cn(
          '-mb-10 flex items-center justify-end pr-1 md:hidden md:w-0',
          {
            hidden: isTridionPreview,
          },
        )}
      >
        <IconButton
          color="secondary"
          onPressEnd={toggleTranslation}
          isDisabled={!showTranslateIcon}
        >
          <IconLanguage />
        </IconButton>
        <div ref={wrapperRef} className="inline-block">
          <IconButton color="secondary" onPressEnd={() => handlePrintMobile()}>
            <IconPrinter />
          </IconButton>
        </div>
        <IconButton color="secondary" onPressEnd={onCopyLink}>
          <IconShare2 />
        </IconButton>
      </div>

      <div
        className={cn(
          'absolute bottom-0 box-border inline-block bg-white p-[3px]',
          'translate-y-[50%] lg:left-10',
          'shadow-elevation-8 rounded-full',
          'left-1/2 -translate-x-1/2 lg:translate-x-0',
        )}
      >
        <div className="border-brand-navy rounded-full border-[3px] border-solid!">
          <div className="flex size-[72px] items-center justify-center">
            {iconContent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TridionTitle;
